import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { SellerEntityStatus, type SellerEntity } from '@etiquetachef/types';
import { subscribeSellerEntities, type SellerEntityListResult } from '@/lib/sellerEntities';

const STATUS_COLOR: Record<string, 'green' | 'grey' | 'blue'> = {
  [SellerEntityStatus.ACTIVE]: 'green',
  [SellerEntityStatus.PENDING]: 'blue',
  [SellerEntityStatus.ARCHIVED]: 'grey',
};

/**
 * Lista das entidades vendedoras (as PJs que vendem e emitem nota), com o PSP
 * de que cada uma é dona.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7). A spec existia desde a Sprint 35
 * (`docs/screens/billing-seller-entities.md`) sem tela; a #756 a implementa
 * para a `etiquetachef-br`.
 *
 * Por quê (#958, owner 2026-10-13): a entidade não tem países — o país escolhe
 * o PSP, e a PJ sai do PSP (`findSellerEntityForProvider` nas Functions). A
 * coluna "Países" lia `MarketConfig.seller_entity_id`, que deixou de existir;
 * a coluna PSP mostra o elo real. Não voltar a listar países aqui.
 *
 * Contrato: só leitura (listener de `/config/sellerEntities/items`); "Criar"
 * e "Editar" levam ao editor
 * (`/billing/seller-entities/new` e `/billing/seller-entities/:id`).
 *
 * Usado por: rota `billing/seller-entities` (`routes/router.tsx`), menu
 * Sistema (`AdminLayout`; estava em Cobrança até 2026-10-01, #756).
 *
 * Histórico:
 *  - 2026-10-13 11:23 +0200 — (#958) coluna "Países" trocada pela do PSP.
 */
export function SellerEntitiesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<SellerEntityListResult>({ kind: 'LOADING' });

  useEffect(() => subscribeSellerEntities(setState), []);

  /** PSP × modo da entidade, no mesmo texto do seletor do editor; sem PSP = "—". */
  const pspLabel = (e: SellerEntity): string => {
    if (!e.payment_provider) return '—';
    return e.payment_mode
      ? t(`billing.markets.pspChoice.${e.payment_provider}.${e.payment_mode}`)
      : t('billing.sellers.fields.paymentProviderBothModes', {
          provider: t(`billing.markets.provider.${e.payment_provider}`),
        });
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('billing.sellers.subtitle')}
          actions={
            <Button
              variant="primary"
              onClick={(): void => {
                void navigate('/billing/seller-entities/new');
              }}
            >
              {t('billing.sellers.create')}
            </Button>
          }
        >
          {t('billing.sellers.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {state.kind === 'ERROR' && <Alert type="error">{t('billing.sellers.errorLoad')}</Alert>}
      {state.kind === 'READY' && (
        <Table<SellerEntity>
          items={state.entities}
          trackBy="id"
          variant="container"
          empty={<Box textAlign="center">{t('billing.sellers.empty')}</Box>}
          columnDefinitions={[
            {
              id: 'id',
              header: t('billing.sellers.columns.id'),
              // O id é um slug longo; sem largura mínima o Cloudscape quebrava
              // letra a letra (visto no teste pela tela, #756).
              minWidth: 180,
              cell: (e): React.ReactNode => (
                <Button
                  variant="inline-link"
                  onClick={(): void => {
                    void navigate(`/billing/seller-entities/${e.id}`);
                  }}
                >
                  {e.id}
                </Button>
              ),
            },
            { id: 'legal', header: t('billing.sellers.columns.legalName'), cell: (e): string => e.legal_name },
            { id: 'hq', header: t('billing.sellers.columns.country'), cell: (e): string => e.headquarter_country },
            { id: 'tax', header: t('billing.sellers.columns.taxId'), cell: (e): string => e.tax_id },
            {
              id: 'status',
              header: t('billing.sellers.columns.status'),
              cell: (e): React.ReactNode => (
                <Badge color={STATUS_COLOR[e.status] ?? 'grey'}>{t(`billing.sellers.status.${e.status}`)}</Badge>
              ),
            },
            {
              id: 'psp',
              header: t('billing.sellers.fields.paymentProvider'),
              cell: pspLabel,
            },
            {
              id: 'fiscal',
              header: t('billing.sellers.columns.nfse'),
              cell: (e): string =>
                e.br_fiscal
                  ? [
                      e.br_fiscal.nfse.live ? t('billing.sellers.nfseLive') : null,
                      e.br_fiscal.nfse.sandbox ? t('billing.sellers.nfseSandbox') : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || t('billing.sellers.nfseNone')
                  : '—',
            },
          ]}
        />
      )}
    </ContentLayout>
  );
}
